import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import Buttons from 'mutation/components/registration-civil-union/buttons';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerRegistrationCivilUnionData } from 'mutation/utils/transformer-registration-civil-union';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerRequestRegistrationCivilUnionData } from 'mutation/utils/transformer-request-registration-civil-union';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/registration-civil-union-validation';
import FormRegistrationCivilUnion from 'mutation/components/registration-civil-union/form';
import { RegistrationCivilUnionFormVM } from 'mutation/view-models/registration-civil-union';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { RegistrationCivilUnionCrumbs } from 'mutation/constants/registration-civil-union-crumbs';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import PmiTitle from 'shell/components/shared/title';

interface RegistrationCivilUnionPageProps {
  isWithoutBreadcrumbs?: boolean;
  onConfirm?: () => void;
  onHandleExit?: () => void;
  initialValues?: RegistrationCivilUnionFormVM;
  goBackUrl?: string;
  disabledFromAct?: boolean;
}

const RegistrationCivilUnionPage = ({
  isWithoutBreadcrumbs = false,
  onConfirm,
  onHandleExit,
  initialValues = {},
  goBackUrl = '/mutazioni/persona/unione-civile/residenti',
}: RegistrationCivilUnionPageProps): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);

  const [queryParams] = useSearchParams();

  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, RegistrationCivilUnionFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerRegistrationCivilUnionData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCivilUnion, registerUnionCivil] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/registrazione-unione-civile/registrazione`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCivilUnion.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  // const [openError, setOpenError] = useState<boolean>(false);

  // useEffect(() => {
  //   const data = anpr.data as RegistrationCivilUnionFormVM;
  //   const canBeMutated = checkPersonRegistrationCivilUnion(data?.civilStatus?.oldStatus);

  //   if (!canBeMutated) {
  //     // persona già coniugata
  //     setOpenError(true);
  //   }
  // }, [anpr.data]);

  const handleSubmit = useEventCallback((values: RegistrationCivilUnionFormVM) => {
    const data = transformerRequestRegistrationCivilUnionData(values);

    (
      registerUnionCivil({ data }) as ResponsePromise<
        HttpResponse<ResponseAnprSaveData>,
        SaveDataAnpr
      >
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;

      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      if (onConfirm) {
        onConfirm();
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  const handleExit = useCallback(() => {
    navigate(goBackUrl);
  }, [navigate]);

  const handleOpenModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  // const onCloseErrorModal = useEventCallback((value: boolean) => {
  //   setOpenError(value);
  //   navigate(goBackUrl);
  // });

  const isLoading = !anpr.data || anpr.isLoading;

  return !isLoading ? (
    <Fragment>
      {/* <ErrorModal
        open={openError}
        onClose={onCloseErrorModal}
        message="Stato civile del soggetto incongruente con la registrazione dell'unione civile"
      /> */}
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={RegistrationCivilUnionCrumbs} />}
      <PmiTitle title="Registrazione Unione Civile" />
      <Formik
        validationSchema={validationSchema}
        initialValues={{ ...((anpr.data || {}) as RegistrationCivilUnionFormVM), ...initialValues }}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Buttons handleOpenModal={handleOpenModal} />
            </Grid>
            <Grid item xs={12} md={10}>
              <SearchMessages
                type={MessageType.Error}
                title={errorMessage.title || ''}
                messages={errorMessage.messages || []}
                visibility={errorMessage.visibility || false}
                onChangeVisibility={onVisibility}
              />
              <SearchMessages
                type={MessageType.Warning}
                title={
                  castedAnprData?.warnings?.length === 1
                    ? `C'è 1 warning`
                    : `Ci sono ${castedAnprData?.warnings?.length} warning`
                }
                messages={castedAnprData?.warnings || []}
                visibility={
                  visibility.warning !== undefined
                    ? visibility.warning
                    : (castedAnprData?.warnings || [])?.length > 0
                }
                onChangeVisibility={onVisibility}
              />
              <FormRegistrationCivilUnion />
            </Grid>
          </Grid>
          <ExitModal
            open={open}
            onClose={() => setOpen(false)}
            onConfirm={() => {
              if (onHandleExit) {
                onHandleExit();
                return;
              }
              handleExit();
            }}
          />
        </Form>
      </Formik>
    </Fragment>
  ) : (
    <></>
  );
};

RegistrationCivilUnionPage.displayName = 'Registration Civil union page component';

export default RegistrationCivilUnionPage;
